import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateTime } from 'luxon';
import {
  NeedDto,
  NeedsActionDto,
  NeedsActivityDto,
  SuggestedActionDto,
} from '@act/shared/data-transfer-objects';
import {
  ActionFormValues,
  NeedActionFormSchema,
} from '@act/react-components/dialog-forms/components/useNeedActionFormtypes';
import { DATE_FORMAT } from '@act/react-components/dialog-forms/ScheduledEventDialog';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { needRequests } from 'redux/requests/need.requests';
import { useToast } from '@act/react-components/app/ReusableToast';
import useUnsavedChangesDialog from '@act/react-components/dialog-forms/hooks/useUnsavedChangesDialog.hook';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hook';
import syvaNeedsUtils from '../../syvaNeeds.util';
import { convertTimeStringToMinutes } from '@act/react-components/dialog-forms/helpers/timeConverter.util';
import { setSidePanelIsDirty } from '@act/react-components/member-page/Calls/FullScreenSaveClose.utils';
import type { AddNeedResourceDetailsToActionRequest } from '@act/shared/types';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';

interface UseAddActionFormProps {
  need: NeedDto;
  action?: NeedsActionDto;
  suggestedAction?: SuggestedActionDto;
  onClose: () => void;
  trackOpenState?: boolean;
}

type ResourceDetailsRequestWithId = AddNeedResourceDetailsToActionRequest & {
  id: number;
};

function formatResourceReferrals(
  resourceRequests: ResourceDetailsRequestWithId[]
): AddNeedResourceDetailsToActionRequest[] {
  return resourceRequests.map(details => {
    const { id, phoneNumber, ...data } = details;
    const formattedPhoneNumber = phoneNumber
      ? '+1'.concat(phoneNumber?.replace(/\D/g, ''))
      : undefined;
    return {
      ...data,
      ...(formattedPhoneNumber && { phoneNumber: formattedPhoneNumber }),
      ...(id > 0 && { id: details.id }),
    };
  });
}

export const useAddActionForm = ({
  need,
  action,
  suggestedAction,
  onClose,
  trackOpenState = false,
}: UseAddActionFormProps) => {
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const [durationFocused, setDurationFocused] = useState(false);
  const toast = useToast();
  const defaultDate = DateTime.now().toFormat(DATE_FORMAT);
  const referralsCountRef = useRef<number>(null);

  const isEditing = Boolean(action);
  const needMetadata = metaSliceSelectors.useSelectNeedsMetadata();
  const { activities, issues } = needMetadata.data;

  const issue = useMemo(
    () => issues.find(i => i.id === need?.issueId),
    [need?.issueId, issues]
  );

  const category = needMetadata.data.categories.find(
    c => c.id === issue?.categoryId
  );

  const daysRemaining = syvaNeedsUtils.calculateRemainingDays(
    need?.expiresAt || 0
  );

  const activityOptions: NeedsActivityDto[] = useMemo(
    () =>
      activities
        .filter(a => issue.activityIds.includes(a.id))
        .sort((a, b) => {
          if (b.label < a.label) return 1;
          if (b.label > a.label) return -1;
          return 0;
        }),
    [activities, issue]
  );

  const { control, handleSubmit, formState, watch, reset, getValues } =
    useForm<ActionFormValues>({
      defaultValues: {
        activityId: action?.activityId || suggestedAction?.activityId || 0,
        date: action?.date || defaultDate,
        duration: action?.durationInMinutes?.toString() ?? '',
        description: action?.description || suggestedAction?.description || '',
        resourceReferred: Boolean(action?.resourceReferrals?.length),
        markAsFulfilled: false,
        resourceDetails:
          action?.resourceReferrals?.map(resource => ({
            id: resource.id,
            resourceName: resource.resourceName,
            contactName: resource.contactName || '',
            phoneNumber: resource.phoneNumber || '',
            url: resource.url || '',
            streetAddress: resource.streetAddress || '',
            city: resource.city || '',
            state: resource.state || '',
            zipcode: resource.zipcode || '',
            shouldDelete: false,
          })) || [],
      },
      resolver: zodResolver(NeedActionFormSchema),
    });

  const {
    fields: resourceDetailsFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'resourceDetails',
    keyName: 'key',
  });

  const resourceReferred = watch('resourceReferred');

  const [createdAction, createActionLoading, createAction, createActionError] =
    useApiCall(needRequests.addActionToNeed);

  useEffect(() => {
    if (action) {
      const allActions = [action, ...(action.subsequentActions || [])];
      allActions.sort((a, b) => b.createdAt - a.createdAt);
      const latestAction = allActions[0];

      const allResourceReferrals = [];

      if (action.resourceReferrals) {
        allResourceReferrals.push(...action.resourceReferrals);
      }

      if (latestAction?.resourceReferrals && latestAction !== action) {
        allResourceReferrals.push(...latestAction.resourceReferrals);
      }

      reset({
        activityId: latestAction?.activityId,
        date: `${latestAction?.date}`,
        duration: latestAction?.durationInMinutes?.toString() ?? '',
        description: latestAction.description,
        resourceReferred: allResourceReferrals.length > 0,
        resourceDetails: allResourceReferrals.map(resource => ({
          id: resource.id,
          resourceName: resource.resourceName,
          contactName: resource.contactName || '',
          phoneNumber: resource.phoneNumber || '',
          url: resource.url || '',
          streetAddress: resource.streetAddress || '',
          city: resource.city || '',
          state: resource.state || '',
          zipcode: resource.zipcode || '',
          shouldDelete: false,
        })),
      });
    } else if (suggestedAction) {
      reset({
        activityId: suggestedAction.activityId,
        date: defaultDate,
        duration: '',
        description: suggestedAction.description,
        resourceReferred: false,
        markAsFulfilled: false,
        resourceDetails: [],
      });
    } else {
      reset({
        activityId: 0,
        date: defaultDate,
        duration: '',
        description: '',
        resourceReferred: false,
        markAsFulfilled: false,
        resourceDetails: [],
      });
    }
  }, [action, suggestedAction, reset, defaultDate]);

  const addResourceDetail = () => {
    append({
      id: undefined,
      resourceName: '',
      contactName: '',
      phoneNumber: '',
      url: '',
      streetAddress: '',
      city: '',
      state: '',
      zipcode: '',
      shouldDelete: false,
    });
  };

  useEffect(() => {
    if (referralsCountRef.current > resourceDetailsFields.length) {
      referralsCountRef.current = referralsCountRef.current - 1;
    }
  }, [resourceDetailsFields.length]);

  useEffect(() => {
    if (resourceDetailsFields.length >= referralsCountRef.current) {
      referralsCountRef.current = resourceDetailsFields.length;
    }
  }, [resourceDetailsFields]);

  const removeResourceDetail = (index: number) => {
    remove(index);
  };

  useEffect(() => {
    if (createdAction) {
      const actionType = createdAction.originalActionId ? 'updated' : 'created';
      toast(`Action ${actionType} successfully`);
      reset();
      onClose();
    }
  }, [createdAction, onClose, reset, toast]);

  useEffect(() => {
    if (createActionError) {
      toast(
        createActionError?.userMessage
          ? `Failed to create or update action. Please try again. ${createActionError?.userMessage}`
          : 'Failed to create or update action. Please try again.',
        'error'
      );
    }
  }, [createActionError, toast]);

  const { isDirty } = formState;

  useEffect(() => {
    if (trackOpenState) {
      setSidePanelIsDirty(isDirty);
    }
  }, [isDirty, trackOpenState]);

  useEffect(() => {
    if (!trackOpenState) {
      setSidePanelIsDirty(false);
    }
    return () => {
      setSidePanelIsDirty(false);
    };
  }, [trackOpenState]);

  const onSubmit = (data: ActionFormValues) => {
    const resources =
      data?.resourceDetails?.length > 0
        ? formatResourceReferrals(
            data?.resourceDetails as ResourceDetailsRequestWithId[]
          )
        : null;
    createAction({
      params: { needId: need.id },
      body: {
        activityId: data.activityId,
        date: data.date,
        durationInMinutes: Number(convertTimeStringToMinutes(data.duration)),
        description: data.description,
        ...(!!suggestedAction &&
          !!suggestedAction.tempNeedId && {
            tempNeedId: suggestedAction.tempNeedId,
          }),
        ...(!!action && {
          originalActionId: action?.originalActionId
            ? action.originalActionId
            : action.id,
        }),
        ...(resources && {
          resourceReferrals: resources,
        }),
        markAsFulfilled: data.markAsFulfilled,
        ...(!!suggestedAction &&
          !!suggestedAction.id && {
            suggestedActionId: suggestedAction.id,
          }),
      },
    });
  };

  const activitySelectOptions = useMemo(() => {
    return activityOptions.map(activity => ({
      label: activity.label,
      value: activity.id,
    }));
  }, [activityOptions]);

  const handleCancel = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        reset();
        onClose();
      });
    } else {
      reset();
      onClose();
    }
  };

  const getFormConfig = (): FormConfig<ActionFormValues> => [
    {
      type: 'input',
      name: 'activityId',
      label: 'Activity',
      input: 'select',
      props: {
        options: activitySelectOptions,
        fullWidth: true,
      },
    },
    {
      type: 'input',
      name: 'date',
      label: 'Date',
      input: 'date',
      props: {
        defaultValue: defaultDate,
        inputType: 'date',
      },
    },
    {
      type: 'input',
      name: 'duration',
      label: 'Duration',
      input: 'text',
      props: {
        required: true,
        iconAdornment:
          !durationFocused && getValues('duration') !== '' ? 'minutes' : '',
        onFocusCallback: () => {
          setDurationFocused(true);
        },
        onBlurCallback: (e, fn) => {
          const value = e.target.value;
          setDurationFocused(false);
          fn(value);
        },
      },
    },
    {
      type: 'input',
      name: 'description',
      label: 'Description',
      input: 'text',
      props: {
        required: true,
        multiline: true,
        minRows: 4,
      },
    },
    {
      type: 'group',
      items: [
        {
          type: 'input',
          name: 'markAsFulfilled',
          label: 'Mark as Fulfilled',
          input: 'checkbox',
        },
        {
          type: 'input',
          name: 'resourceReferred',
          label: 'Resource Referred',
          input: 'checkbox',
        },
      ],
    },
  ];

  return {
    control,
    handleSubmit,
    formState,
    watch,
    reset,
    getValues,
    resourceDetailsFields,
    addResourceDetail,
    removeResourceDetail,
    resourceReferred,
    createActionLoading,
    onSubmit,
    activitySelectOptions,
    handleCancel,
    isEditing,
    isDirty,
    issue,
    category,
    daysRemaining,
    defaultDate,
    durationFocused,
    setDurationFocused,
    getFormConfig,
  };
};
